import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useAdminTenant, useCreateTenant, useUpdateTenant } from '@/hooks/use-admin'

interface AuthConfig {
  clientId?: string
  clientSecret?: string
  tenantId?: string
  issuer?: string
  entityId?: string
  ssoUrl?: string
  certificate?: string
}

interface TenantFormValues {
  name: string
  slug: string
  authProvider: string
  authConfig: AuthConfig
  isActive: boolean
  maxUsers: number | null
  maxStorage: number | null
}

const AUTH_CONFIG_FIELDS: Record<
  string,
  { key: keyof AuthConfig; label: string; type?: string }[]
> = {
  'azure-ad': [
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    { key: 'tenantId', label: 'Tenant ID' },
  ],
  google: [
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
  ],
  oidc: [
    { key: 'issuer', label: 'Issuer URL' },
    { key: 'clientId', label: 'Client ID' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
  ],
  saml: [
    { key: 'entityId', label: 'Entity ID' },
    { key: 'ssoUrl', label: 'SSO URL' },
    { key: 'certificate', label: 'Certificate' },
  ],
}

const AUTH_PROVIDERS = [
  { value: 'azure-ad', label: 'Azure AD' },
  { value: 'google', label: 'Google' },
  { value: 'oidc', label: 'OIDC' },
  { value: 'saml', label: 'SAML' },
] as const

const STORAGE_OPTIONS = [
  { value: 1073741824, label: '1 GB' },
  { value: 5368709120, label: '5 GB' },
  { value: 10737418240, label: '10 GB' },
  { value: 53687091200, label: '50 GB' },
  { value: 107374182400, label: '100 GB' },
] as const

function _formatBytes(bytes: number | null): string {
  if (!bytes) return '-'
  const gb = bytes / (1024 * 1024 * 1024)
  return gb >= 1 ? `${gb} GB` : `${(bytes / (1024 * 1024)).toFixed(0)} MB`
}

export default function TenantFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: tenant, isLoading: tenantLoading } = useAdminTenant(id)
  const createTenant = useCreateTenant()
  const updateTenant = useUpdateTenant()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TenantFormValues>({
    defaultValues: {
      name: '',
      slug: '',
      authProvider: 'azure-ad',
      authConfig: {},
      isActive: true,
      maxUsers: 100,
      maxStorage: 10737418240,
    },
  })

  const isActive = watch('isActive')
  const authProvider = watch('authProvider')
  const maxStorage = watch('maxStorage')

  useEffect(() => {
    if (isEdit && tenant) {
      const t = tenant as any
      reset({
        name: t.name ?? '',
        slug: t.slug ?? '',
        authProvider: t.authProvider ?? 'azure-ad',
        authConfig: t.authConfig ?? {},
        isActive: t.isActive ?? true,
        maxUsers: t.maxUsers ?? 100,
        maxStorage: t.maxStorage ?? 10737418240,
      })
    }
  }, [isEdit, tenant, reset])

  const onSubmit = async (data: TenantFormValues) => {
    try {
      const cleanAuthConfig = Object.fromEntries(
        Object.entries(data.authConfig).filter(([, v]) => v !== '' && v !== undefined),
      )
      const payload = {
        ...data,
        authConfig: cleanAuthConfig,
        maxUsers: data.maxUsers ? Number(data.maxUsers) : null,
        maxStorage: data.maxStorage ? Number(data.maxStorage) : null,
      }
      if (isEdit) {
        await updateTenant.mutateAsync({ id: id!, data: payload })
        toast.success('テナントを更新しました')
      } else {
        await createTenant.mutateAsync({
          name: payload.name,
          slug: payload.slug,
          authProvider: payload.authProvider,
          authConfig: payload.authConfig,
          maxUsers: payload.maxUsers ?? undefined,
        })
        toast.success('テナントを作成しました')
      }
      navigate('/admin/tenants')
    } catch {
      toast.error(isEdit ? '更新に失敗しました' : '作成に失敗しました')
    }
  }

  if (isEdit && tenantLoading) {
    return (
      <div className="container mx-auto max-w-2xl py-6">
        <Skeleton className="mb-6 h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-2xl py-6">
      <h1 className="mb-6 text-2xl font-bold">{isEdit ? 'テナント編集' : 'テナント作成'}</h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 基本情報 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">基本情報</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">テナント名 *</Label>
              <Input id="name" {...register('name', { required: 'テナント名は必須です' })} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug (URL識別子) *</Label>
              <Input
                id="slug"
                {...register('slug', {
                  required: 'Slugは必須です',
                  pattern: { value: /^[a-z0-9-]+$/, message: '英小文字・数字・ハイフンのみ' },
                })}
              />
              {errors.slug && <p className="text-sm text-destructive">{errors.slug.message}</p>}
              <p className="text-xs text-muted-foreground">
                URLに使用されます: /{watch('slug') || 'example'}/...
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>有効/無効</Label>
                <p className="text-xs text-muted-foreground">
                  無効にするとテナントのユーザーはログインできません
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={(checked) => setValue('isActive', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* 認証設定 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">認証設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>認証プロバイダー</Label>
              <Select
                value={authProvider}
                onValueChange={(value) => {
                  setValue('authProvider', value)
                  setValue('authConfig', {})
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {AUTH_CONFIG_FIELDS[authProvider]?.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={`authConfig.${field.key}`}>{field.label}</Label>
                <Input
                  id={`authConfig.${field.key}`}
                  type={field.type ?? 'text'}
                  {...register(`authConfig.${field.key}`)}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* リソース制限 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">リソース制限</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="maxUsers">最大ユーザー数</Label>
              <Input
                id="maxUsers"
                type="number"
                min={1}
                {...register('maxUsers', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-2">
              <Label>最大ストレージ</Label>
              <Select
                value={maxStorage?.toString() ?? ''}
                onValueChange={(value) => setValue('maxStorage', Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {STORAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* アクション */}
        <div className="flex gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : isEdit ? '更新' : '作成'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/tenants')}>
            キャンセル
          </Button>
        </div>
      </form>
    </div>
  )
}
